import type { Squad } from '../../../types';
import type { OrganizationSeason, PartnerSeason } from '../types';
import { organizationSeasonForTerm } from './organizationSeasons';

export type SeasonHealthStatus = 'on_track' | 'attention' | 'at_risk' | 'blocked' | 'complete';
export type SeasonPhase =
  | 'planning'
  | 'pre_registration'
  | 'registration_open'
  | 'launch_preparation'
  | 'running'
  | 'wrap_up'
  | 'complete';

export type DerivedSeasonMilestone = {
  id: 'marketing_launch' | 'registration_open' | 'enrollment_review' | 'registration_close' | 'renewal_outreach';
  label: string;
  date: string | null;
  source: string;
};

export type DerivedSeasonSquad = {
  squad: Squad;
  decidedNotToRun: boolean;
  startDate: string | null;
  endDate: string | null;
  hasUsableSchedule: boolean;
  scheduleStatus: 'draft' | 'proposed' | 'confirmed' | 'changed' | 'canceled';
  teacherReady: boolean;
  daysUntilStart: number | null;
  health: SeasonHealthStatus;
  reason: string;
};

export type DerivedPartnerSeason = {
  seasonId: string;
  phase: SeasonPhase;
  health: SeasonHealthStatus;
  calculatedHealth: SeasonHealthStatus;
  healthReason: string;
  healthOverrideActive: boolean;
  squads: DerivedSeasonSquad[];
  readySquadCount: number;
  operatingStartDate: string | null;
  operatingEndDate: string | null;
  milestones: DerivedSeasonMilestone[];
  nextAction: { label: string; dueDate: string | null; scope: 'season' | 'squad'; squadId?: string } | null;
  warnings: string[];
  blockedMilestoneCount: number;
  effectiveSeason: PartnerSeason;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function validYmd(value: unknown): string | null {
  const raw = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const date = new Date(`${raw}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : raw;
}

function shiftDays(ymd: string | null, days: number): string | null {
  if (!ymd) return null;
  const date = new Date(`${ymd}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromYmd: string, toYmd: string): number {
  return Math.round((new Date(`${toYmd}T00:00:00.000Z`).getTime() - new Date(`${fromYmd}T00:00:00.000Z`).getTime()) / DAY_MS);
}

function dateForSquad(squad: Squad, side: 'start' | 'end'): string | null {
  const mainDate = validYmd(squad.schedule?.mainDate);
  if (mainDate) return mainDate;
  return validYmd(side === 'start'
    ? squad.schedule?.startDate || squad.firstDate
    : squad.schedule?.endDate || squad.lastDate);
}

/** Resolve the dates shared by every season surface. Squad schedules become
 * operational truth; organization dates remain the pre-squad scaffold. */
export function resolvePartnerSeasonDates(
  season: PartnerSeason,
  allPartnerSquads: Squad[],
  organizationSeason?: OrganizationSeason | Omit<OrganizationSeason, 'id'> | null,
): PartnerSeason {
  const org = organizationSeason || organizationSeasonForTerm(season.termId || season.id);
  const seasonSquads = allPartnerSquads.filter((squad) => squadBelongsToSeason(squad, season) && !squadWasDecidedNotToRun(squad));
  const starts = seasonSquads.map((squad) => dateForSquad(squad, 'start')).filter((value): value is string => Boolean(value)).sort();
  const ends = seasonSquads.map((squad) => dateForSquad(squad, 'end')).filter((value): value is string => Boolean(value)).sort();
  const squadStart = starts[0] || null;
  const squadEnd = ends[ends.length - 1] || null;
  const registrationIsOverride = season.dateSources?.registrationStartDate === 'partner_override'
    || season.dateSources?.registrationEndDate === 'partner_override';
  const explicitRegistration = validYmd(season.registrationStartDate) && validYmd(season.registrationEndDate);

  return {
    ...season,
    planningPeriodStartDate: validYmd(season.planningPeriodStartDate) || validYmd(org?.planningPeriodStartDate) || null,
    planningPeriodEndDate: validYmd(season.planningPeriodEndDate) || validYmd(org?.planningPeriodEndDate) || null,
    registrationStartDate: registrationIsOverride && explicitRegistration
      ? validYmd(season.registrationStartDate)
      : shiftDays(squadStart, -28) || validYmd(season.registrationStartDate) || validYmd(org?.registrationStartDate) || null,
    registrationEndDate: registrationIsOverride && explicitRegistration
      ? validYmd(season.registrationEndDate)
      : shiftDays(squadStart, -1) || validYmd(season.registrationEndDate) || validYmd(org?.registrationEndDate) || null,
    defaultProgramStartDate: squadStart || validYmd(season.defaultProgramStartDate) || validYmd(org?.defaultProgramStartDate) || null,
    defaultProgramEndDate: squadEnd || validYmd(season.defaultProgramEndDate) || validYmd(org?.defaultProgramEndDate) || null,
  };
}

function squadBelongsToSeason(squad: Squad, season: PartnerSeason): boolean {
  return String(squad.seasonId || squad.termId || '').trim() === String(season.termId || season.id).trim();
}

/** A canceled squad was explicitly set up but did not run. Keep it visible for
 * historical/retention context, but exclude it from season operations. */
function squadWasDecidedNotToRun(squad: Squad): boolean {
  return [squad.status, squad.scheduleStatus]
    .some((status) => ['canceled', 'cancelled'].includes(String(status || '').toLowerCase()));
}

function statusRank(status: SeasonHealthStatus): number {
  return { complete: 0, on_track: 1, attention: 2, at_risk: 3, blocked: 4 }[status];
}

function deriveSquad(squad: Squad, today: string): DerivedSeasonSquad {
  const decidedNotToRun = squadWasDecidedNotToRun(squad);
  const startDate = dateForSquad(squad, 'start');
  const endDate = dateForSquad(squad, 'end');
  const hasUsableSchedule = Boolean(
    squad.scheduleKind === 'single_day' || squad.schedule?.mainDate
      ? startDate && squad.schedule?.startTime && squad.schedule?.endTime
      : startDate && endDate && squad.schedule?.dayOfWeek && squad.schedule?.startTime && squad.schedule?.endTime,
  );
  const rawScheduleStatus = squad.scheduleStatus;
  const scheduleStatus: DerivedSeasonSquad['scheduleStatus'] = decidedNotToRun
    ? 'canceled'
    : rawScheduleStatus === 'proposed'
      ? 'proposed'
      : hasUsableSchedule
        ? 'confirmed'
        : 'draft';
  // teacherIds grants classroom access and may include supporting or observing staff.
  // Operational staffing is ready only when one person explicitly owns the lead role.
  const teacherReady = Boolean(String(squad.leadTeacherId || '').trim());
  const daysUntilStart = startDate ? daysBetween(today, startDate) : null;

  if (scheduleStatus === 'canceled') {
    return { squad, decidedNotToRun, startDate, endDate, hasUsableSchedule, scheduleStatus: 'canceled', teacherReady, daysUntilStart, health: 'complete', reason: 'Canceled' };
  }
  if (!hasUsableSchedule) {
    return { squad, decidedNotToRun, startDate, endDate, hasUsableSchedule, scheduleStatus, teacherReady, daysUntilStart, health: 'attention', reason: 'Schedule incomplete' };
  }
  if (daysUntilStart !== null && daysUntilStart <= 28 && !teacherReady) {
    return { squad, decidedNotToRun, startDate, endDate, hasUsableSchedule, scheduleStatus, teacherReady, daysUntilStart, health: 'at_risk', reason: `No lead teacher ${daysUntilStart < 0 ? 'after start' : `within ${daysUntilStart} days of start`}` };
  }
  if (daysUntilStart !== null && daysUntilStart <= 42 && scheduleStatus !== 'confirmed') {
    return { squad, decidedNotToRun, startDate, endDate, hasUsableSchedule, scheduleStatus, teacherReady, daysUntilStart, health: 'attention', reason: 'Schedule is not confirmed' };
  }
  return { squad, decidedNotToRun, startDate, endDate, hasUsableSchedule, scheduleStatus, teacherReady, daysUntilStart, health: 'on_track', reason: 'Schedule and staffing are on track' };
}

function derivePhase(season: PartnerSeason, operatingStart: string | null, operatingEnd: string | null, today: string): SeasonPhase {
  const registrationStart = validYmd(season.registrationStartDate);
  const registrationEnd = validYmd(season.registrationEndDate);
  const planningStart = validYmd(season.planningPeriodStartDate);
  const start = operatingStart || validYmd(season.defaultProgramStartDate);
  const end = operatingEnd || validYmd(season.defaultProgramEndDate);
  if (end && today > end) return 'complete';
  if (start && end && today >= start && today <= end) return 'running';
  if (start && today < start && (!registrationEnd || today > registrationEnd)) return 'launch_preparation';
  if (registrationStart && registrationEnd && today >= registrationStart && today <= registrationEnd) return 'registration_open';
  if (registrationStart && today < registrationStart) return planningStart && today >= planningStart ? 'pre_registration' : 'planning';
  if (end && today > end) return 'wrap_up';
  return 'planning';
}

export function derivePartnerSeason(
  season: PartnerSeason,
  allPartnerSquads: Squad[],
  now: Date = new Date(),
  organizationSeason?: OrganizationSeason | Omit<OrganizationSeason, 'id'> | null,
): DerivedPartnerSeason {
  const orgScaffold = organizationSeason || organizationSeasonForTerm(season.termId || season.id);
  const expectedProgramStart = validYmd(season.defaultProgramStartDate) || validYmd(orgScaffold?.defaultProgramStartDate);
  const expectedProgramEnd = validYmd(season.defaultProgramEndDate) || validYmd(orgScaffold?.defaultProgramEndDate);
  season = resolvePartnerSeasonDates(season, allPartnerSquads, organizationSeason);
  const today = now.toISOString().slice(0, 10);
  const squads = allPartnerSquads.filter((squad) => squadBelongsToSeason(squad, season)).map((squad) => deriveSquad(squad, today));
  const operationalSquads = squads.filter((item) => !item.decidedNotToRun);
  const activeSquads = operationalSquads.filter((item) => item.scheduleStatus !== 'canceled' && item.squad.status !== 'canceled');
  const starts = activeSquads.map((item) => item.startDate).filter((value): value is string => Boolean(value)).sort();
  const ends = activeSquads.map((item) => item.endDate).filter((value): value is string => Boolean(value)).sort();
  const operatingStartDate = starts[0] || null;
  const operatingEndDate = ends[ends.length - 1] || null;
  const registrationStart = validYmd(season.registrationStartDate);
  const registrationEnd = validYmd(season.registrationEndDate);
  const effectiveEnd = operatingEndDate || validYmd(season.defaultProgramEndDate);
  const milestones: DerivedSeasonMilestone[] = [
    { id: 'marketing_launch', label: 'Marketing launch', date: shiftDays(registrationStart, -14), source: '14 days before registration opens' },
    { id: 'registration_open', label: 'Registration opens', date: registrationStart, source: 'Registration window' },
    { id: 'enrollment_review', label: 'Enrollment review', date: shiftDays(registrationEnd, -14), source: '14 days before registration closes' },
    { id: 'registration_close', label: 'Registration closes', date: registrationEnd, source: 'Registration window' },
    { id: 'renewal_outreach', label: 'Renewal outreach', date: shiftDays(effectiveEnd, -14), source: operatingEndDate ? '14 days before actual operating end' : '14 days before default program end' },
  ];

  const warnings: string[] = [];
  if (!registrationStart || !registrationEnd) warnings.push('Registration window is incomplete.');
  if (!validYmd(season.defaultProgramStartDate) || !validYmd(season.defaultProgramEndDate)) warnings.push('Default program window is incomplete.');
  if (registrationStart && registrationEnd && registrationEnd < registrationStart) warnings.push('Registration closes before it opens.');
  if (validYmd(season.defaultProgramStartDate) && validYmd(season.defaultProgramEndDate) && String(season.defaultProgramEndDate) < String(season.defaultProgramStartDate)) warnings.push('Default program window ends before it starts.');
  if (operatingStartDate && registrationEnd && operatingStartDate < registrationEnd) warnings.push('A squad starts before registration closes.');
  const expectedStart = expectedProgramStart;
  const expectedEnd = expectedProgramEnd;
  if (operatingStartDate && expectedStart && operatingStartDate < expectedStart) warnings.push(`Operating window starts ${operatingStartDate}, before this season’s expected ${expectedStart} start.`);
  if (operatingEndDate && expectedEnd && operatingEndDate > expectedEnd) warnings.push(`Operating window ends ${operatingEndDate}, after this season’s expected ${expectedEnd} end. Review squad season assignments or adjust the partner dates.`);
  operationalSquads.filter((item) => !item.hasUsableSchedule).forEach((item) => warnings.push(`${item.squad.title}: schedule incomplete.`));

  const worstSquad = operationalSquads.slice().sort((a, b) => statusRank(b.health) - statusRank(a.health))[0];
  const phase = derivePhase(season, operatingStartDate, operatingEndDate, today);
  const isCommitted = ['verbal', 'written', 'contract'].includes(season.commitment?.mode || '');
  let calculatedHealth: SeasonHealthStatus = 'on_track';
  let healthReason = 'Required season dates and current squads are on track.';
  if (phase === 'complete') {
    calculatedHealth = 'complete';
    healthReason = 'The operating window has ended.';
  } else if (squads.length > 0 && operationalSquads.length === 0) {
    calculatedHealth = 'complete';
    healthReason = 'All squads created for this season were canceled.';
  } else if (!registrationStart || !registrationEnd || !validYmd(season.defaultProgramStartDate) || !validYmd(season.defaultProgramEndDate)) {
    calculatedHealth = 'attention';
    healthReason = 'Required season windows are incomplete.';
  } else if (isCommitted && operationalSquads.length === 0) {
    const deliveryWorkShouldExist = ['registration_open', 'launch_preparation', 'running', 'wrap_up'].includes(phase);
    calculatedHealth = deliveryWorkShouldExist ? 'at_risk' : 'attention';
    healthReason = deliveryWorkShouldExist
      ? 'Committed, but no squads or program logistics have been entered.'
      : 'Committed, but no squads have been entered yet.';
  }
  if (worstSquad && statusRank(worstSquad.health) > statusRank(calculatedHealth)) {
    calculatedHealth = worstSquad.health;
    healthReason = `${worstSquad.squad.title}: ${worstSquad.reason}.`;
  }

  const override = season.healthOverride;
  const healthOverrideActive = Boolean(override?.status && validYmd(override.expiresOn) && String(override.expiresOn) >= today);
  const health = healthOverrideActive ? override!.status : calculatedHealth;
  if (healthOverrideActive) healthReason = `Override: ${override!.reason}`;

  const squadAction = operationalSquads
    .filter((item) => item.health === 'at_risk' || item.health === 'blocked' || item.health === 'attention')
    .sort((a, b) => statusRank(b.health) - statusRank(a.health) || (a.startDate || '9999').localeCompare(b.startDate || '9999'))[0];
  const missingMilestone = milestones.find((milestone) => !milestone.date);
  const nextDatedMilestone = milestones.filter((milestone) => milestone.date && milestone.date >= today).sort((a, b) => String(a.date).localeCompare(String(b.date)))[0];
  const nextAction = isCommitted && squads.length === 0
    ? { label: `Create the first squad for ${season.label || season.termId}`, dueDate: registrationStart, scope: 'season' as const }
    : squadAction
    ? { label: !squadAction.hasUsableSchedule
      ? `Complete ${squadAction.squad.title} schedule`
      : !squadAction.teacherReady
        ? `Assign a lead teacher to ${squadAction.squad.title}`
        : squadAction.scheduleStatus !== 'confirmed'
          ? `Confirm ${squadAction.squad.title} schedule`
          : `Review ${squadAction.squad.title}`,
      dueDate: squadAction.startDate, scope: 'squad' as const, squadId: squadAction.squad.id }
    : missingMilestone
      ? { label: `Set dates needed to calculate ${missingMilestone.label.toLowerCase()}`, dueDate: null, scope: 'season' as const }
      : nextDatedMilestone
        ? { label: nextDatedMilestone.label, dueDate: nextDatedMilestone.date, scope: 'season' as const }
        : null;

  return {
    seasonId: season.termId || season.id,
    phase,
    health,
    calculatedHealth,
    healthReason,
    healthOverrideActive,
    squads,
    readySquadCount: operationalSquads.filter((item) => item.health === 'on_track' || item.health === 'complete').length,
    operatingStartDate,
    operatingEndDate,
    milestones,
    nextAction,
    warnings: Array.from(new Set(warnings)),
    blockedMilestoneCount: milestones.filter((milestone) => !milestone.date).length,
    effectiveSeason: season,
  };
}

export function seasonHealthLabel(status: SeasonHealthStatus): string {
  return ({ on_track: 'On track', attention: 'Attention', at_risk: 'At risk', blocked: 'Blocked', complete: 'Complete' })[status];
}

export function seasonPhaseLabel(phase: SeasonPhase): string {
  return ({
    planning: 'Planning',
    pre_registration: 'Pre-registration',
    registration_open: 'Registration open',
    launch_preparation: 'Launch preparation',
    running: 'Running',
    wrap_up: 'Wrap-up',
    complete: 'Complete',
  })[phase];
}
